import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Avatar, Checkbox, Tooltip, Typography } from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import MessageIcon from '@material-ui/icons/Message';
import { format, parseISO, isPast, isWithinInterval, addDays, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import KanbanCardModal from './KanbanCardModal';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';

const useStyles = makeStyles(theme => ({
  card: {
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    marginBottom: 8,
    padding: '10px 12px',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s',
    border: '1px solid #e2e8f0',
    position: 'relative',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
  },
  labels: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginBottom: 6,
  },
  label: {
    height: 8,
    borderRadius: 4,
    minWidth: 36,
    display: 'inline-block',
  },
  contactName: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#172b4d',
    lineHeight: 1.2,
    marginBottom: 2,
  },
  ticketMeta: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginBottom: 6,
  },
  preview: {
    fontSize: '0.8rem',
    color: '#475569',
    marginBottom: 8,
    lineHeight: 1.4,
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  dueBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    fontSize: '0.72rem',
    fontWeight: 600,
    borderRadius: 4,
    padding: '2px 6px',
  },
  dueBadgeOk: {
    background: '#f0fdf4',
    color: '#15803d',
  },
  dueBadgeSoon: {
    background: '#fefce8',
    color: '#a16207',
  },
  dueBadgeOverdue: {
    background: '#fef2f2',
    color: '#dc2626',
  },
  checklistBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    fontSize: '0.72rem',
    color: '#64748b',
    background: '#f1f5f9',
    borderRadius: 4,
    padding: '2px 6px',
  },
  unreadBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    fontSize: '0.72rem',
    color: '#fff',
    background: '#16a34a',
    borderRadius: 4,
    padding: '2px 6px',
    fontWeight: 700,
  },
  valueBadge: {
    fontSize: '0.72rem',
    color: '#0369a1',
    fontWeight: 700,
    background: '#e0f2fe',
    borderRadius: 4,
    padding: '2px 6px',
  },
  avatar: {
    width: 22,
    height: 22,
    fontSize: '0.6rem',
    marginLeft: 'auto',
  },
  searchDimmed: {
    opacity: 0.35,
  },
  checklistSection: {
    borderTop: '1px solid #e2e8f0',
    marginTop: 8,
    paddingTop: 6,
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    cursor: 'pointer',
    borderRadius: 4,
    padding: '1px 0',
    '&:hover': {
      background: '#f8fafc',
    },
  },
  checklistText: {
    fontSize: '0.78rem',
    lineHeight: 1.3,
    userSelect: 'none',
  },
  checklistProgress: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    fontSize: '0.72rem',
    color: '#64748b',
    background: '#f1f5f9',
    borderRadius: 4,
    padding: '2px 6px',
  },
}));

// Converte dueDate para Date local usando apenas YYYY-MM-DD,
// evitando que UTC midnight seja convertido para o dia anterior no fuso do Brasil.
const parseLocalDate = dueDate => {
  if (!dueDate) return null;
  const s = typeof dueDate === 'string' ? dueDate : dueDate.toISOString();
  const [y, mo, d] = s.substring(0, 10).split('-').map(Number);
  return new Date(y, mo - 1, d); // meia-noite no fuso local
};

const getDueBadgeClass = (dueDate, classes) => {
  if (!dueDate) return null;
  const d = parseLocalDate(dueDate);
  if (isPast(d) && !isSameDay(d, new Date())) return classes.dueBadgeOverdue;
  if (isWithinInterval(d, { start: new Date(), end: addDays(new Date(), 2) })) return classes.dueBadgeSoon;
  return classes.dueBadgeOk;
};

const formatDue = dueDate => {
  const d = parseLocalDate(dueDate);
  if (isSameDay(d, new Date())) return 'Hoje';
  return format(d, 'dd MMM', { locale: ptBR });
};

const KanbanCard = props => {
  const { ticket, opportunityValue, onClick } = props;
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const [checklist, setChecklist] = useState([]);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    let cancelled = false;
    api.get(`/kanban/checklist/${ticket.id}`)
      .then(({ data }) => { if (!cancelled) setChecklist(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ticket.id]);

  // Atualiza checklist quando alterado via Trello ou outro usuário
  useEffect(() => {
    if (!socket || !user?.companyId) return;
    const event = `company-${user.companyId}-kanban-checklist`;
    const handler = data => {
      if (data.ticketId === ticket.id) {
        api.get(`/kanban/checklist/${ticket.id}`)
          .then(({ data: items }) => setChecklist(items))
          .catch(() => {});
      }
    };
    socket.on(event, handler);
    return () => socket.off(event, handler);
  }, [socket, user?.companyId, ticket.id]);

  const handleCheckToggle = async (e, item) => {
    e.stopPropagation();
    const newChecked = !item.checked;
    // Atualização otimista
    setChecklist(prev => prev.map(c => c.id === item.id ? { ...c, checked: newChecked } : c));
    try {
      await api.put(`/kanban/checklist/${item.id}`, { checked: newChecked });
    } catch {
      // Reverte em caso de erro
      setChecklist(prev => prev.map(c => c.id === item.id ? { ...c, checked: !newChecked } : c));
    }
  };

  if (!ticket) return null;

  const handleClick = e => {
    e.stopPropagation();
    setModalOpen(true);
  };

  const checkedCount = checklist.filter(c => c.checked).length;
  const allDone = checklist.length > 0 && checkedCount === checklist.length;

  const dueBadgeClass = ticket.dueDate ? getDueBadgeClass(ticket.dueDate, classes) : null;

  const queueLabel = ticket.queue
    ? { color: ticket.queue.color || '#6366f1', name: ticket.queue.name }
    : null;

  const extraLabels = (ticket.tags || [])
    .filter(t => !t.kanban)
    .slice(0, 3);

  const unread = Number(ticket.unreadMessages) || 0;

  return (
    <>
      <div
        className={classes.card}
        onClick={handleClick}
        style={{
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #e2e8f0',
          padding: '10px 12px',
          marginBottom: 8,
          cursor: 'pointer',
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        {(queueLabel || extraLabels.length > 0) && (
          <div className={classes.labels}>
            {queueLabel && (
              <Tooltip title={queueLabel.name}>
                <span
                  className={classes.label}
                  style={{ background: queueLabel.color, minWidth: 48 }}
                />
              </Tooltip>
            )}
            {extraLabels.map(tag => (
              <Tooltip key={tag.id} title={tag.name}>
                <span
                  className={classes.label}
                  style={{ background: tag.color || '#94a3b8', minWidth: 36 }}
                />
              </Tooltip>
            ))}
          </div>
        )}

        <Typography className={classes.contactName}>
          {ticket.contact?.name || '—'}
        </Typography>

        <Typography className={classes.ticketMeta}>
          #{ticket.id}
          {ticket.queue && ` · ${ticket.queue.name}`}
        </Typography>

        {ticket.lastMessage && (
          <Typography className={classes.preview}>
            {ticket.lastMessage}
          </Typography>
        )}

        {checklist.length > 0 && (
          <div className={classes.checklistSection} onClick={e => e.stopPropagation()}>
            {checklist.map(item => (
              <div
                key={item.id}
                className={classes.checklistItem}
                onClick={e => handleCheckToggle(e, item)}
              >
                <Checkbox
                  size="small"
                  checked={!!item.checked}
                  color="primary"
                  style={{ padding: '2px 4px 2px 0' }}
                  onClick={e => e.stopPropagation()}
                  onChange={e => handleCheckToggle(e, item)}
                />
                <span
                  className={classes.checklistText}
                  style={{
                    textDecoration: item.checked ? 'line-through' : 'none',
                    color: item.checked ? '#94a3b8' : '#374151',
                  }}
                >
                  {item.title}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className={classes.footer}>
          {ticket.dueDate && dueBadgeClass && (
            <span className={`${classes.dueBadge} ${dueBadgeClass}`}>
              <AccessTimeIcon style={{ fontSize: 11 }} />
              {formatDue(ticket.dueDate)}
            </span>
          )}

          {checklist.length > 0 && (
            <span
              className={classes.checklistProgress}
              style={allDone ? { background: '#dcfce7', color: '#15803d' } : {}}
            >
              ☑ {checkedCount}/{checklist.length}
            </span>
          )}

          {opportunityValue > 0 && (
            <span className={classes.valueBadge}>
              R${' '}
              {opportunityValue.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          )}

          {unread > 0 && (
            <span className={classes.unreadBadge}>
              <MessageIcon style={{ fontSize: 11 }} />
              {unread}
            </span>
          )}

          {ticket.user && (
            <Tooltip title={ticket.user.name || ''}>
              <Avatar
                src={ticket.user.urlPicture}
                className={classes.avatar}
                style={{ marginLeft: 'auto' }}
              >
                {(ticket.user.name || '?')[0].toUpperCase()}
              </Avatar>
            </Tooltip>
          )}
        </div>
      </div>

      {modalOpen && (
        <KanbanCardModal
          ticket={ticket}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

export default KanbanCard;
